import React, { useState } from 'react';
import PropTypes from 'prop-types';
import i18n from '@/i18n';

import { Alert, Box, Button, Container, Typography } from '@mui/joy';
import { TextField } from '@/components/InputField';
import validbarcode from "barcode-validator";

import { SelectRelatedNodes } from '@/components/SelectRelatedNodes';

import { SelectRelatedNode } from '@/components/SelectRelatedNode';
import { Loading } from '@/components/Loading';
import { useParams } from 'react-router-dom';
import { Check, Lock } from '@phosphor-icons/react';
import { useHasFeatureFlag, useResource } from '@/hooks';
import { useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { ArtworkField } from '@/components/ArtworkField';
import { uploadCoverArtToRelease } from '@/actions/login';
import { HBox } from '@/components/HBox';
import { submitReleaseForReview, updateRelease } from '@/actions/aloader';
import { FeatureCard } from '@/components/FeatureCard';
import { VBox } from '@/components/VBox';
import { useManager } from '@/hooks';
import ErrorPage from '@/pages/error';
import LoadingPage from '@/pages/loading';
import { useMutation } from '@tanstack/react-query';
import { CopyrightField } from '@/components/CopyrightField';

export function EditReleasePage() {
  const { releaseId } = useParams()
  return (
    <React.Suspense fallback={<Loading />}>
      <EditReleaseView releaseId={releaseId} />
    </React.Suspense>
  )
}

export function ReleaseForm({
  values: defaultValues,
  submitLabel,
  distributing,
  imageUploadError,
  onDistribute = () => {},
  release,
  saving,
  actions,
  onSubmit = () => console.error('onSubmit not implemented'),
  onValuesChange = () => {}
}) {
  const manager = useManager()
  const [errors, setErrors] = useState([])

  const getFieldErrors = (fieldId) => errors?.filter(i => i.field === fieldId)

  const [values, setValues] = useState(defaultValues)

  const canEditCopyrightFields = useHasFeatureFlag('aloaded_studio_label_fields')

  const handleDistributeClick = event => {
    return onDistribute(event)
  }

  const validateForm = () => {
    let errors = []

    if (!values?.primary_genre) {
      errors.push({
        field: 'primary_genre',
        description: i18n.t('release-must-have-a-genre'),
        type: 'missing'
      })
    }

    if (!values?.name) {
      errors.push({
        field: 'name',
        description: i18n.t('release-must-have-a-name'),
        type: i18n.t('empty')
      })
    }

    if (values?.upc?.length > 0 && !validbarcode(values.upc)) {
      errors.push({
        field: 'upc',
        description: i18n.t('invalid-upc-code'),
        type: i18n.t('invalid')
      })
    }
  
    if (!values?.released) {
      errors.push({
        field: 'released',
        description: i18n.t('we-need-a-release-date-for-this-release'),
        type: 'missing'
      })
    }
    
    if (!(values?.tracks?.length > 0)) {
      errors.push({
        field: 'tracks',
        description: i18n.t('release-is-missing-tracks'),
        type: 'missing'
      })
    }

    if (!(values?.release_artists?.length > 0)) {
      errors.push({
        field: 'release_artists',
        description: i18n.t('missing-release-artists'),
        type: 'missing'
      })
    }
    setErrors(errors)
    return true // errors?.length < 1
  }

  const [imageUrl, setImageUrl] = useState(null)

  const handleImageChange = file => {
    const reader = new FileReader()
    reader.onload = (e) => {
      setImageUrl(e.target.result)
    }
    reader.readAsDataURL(file)
    setValues({ ...values, image: file })
  }

  const hasImage = values?.image || values?.image_url

  useEffect(() => {
    setValues(defaultValues)
  }, [defaultValues])

  useEffect(() => {
    onValuesChange(values)
  }, [values, onValuesChange])

  const handleSubmit = (event) => {
    event.preventDefault()
    if (validateForm()) {
      onSubmit(values)
    }
    return false    
  }

  return (
    <form style={{ opacity: saving ? 0.2 : 1, marginBottom: 10 }} method="POST" onSubmit={handleSubmit}>
      <Box>
        <VBox sx={{ gap: 1 }}>
          {values?.locked && (
            <FeatureCard
              sx={{ marginTop: 2, p: 1, marginBottom: 2 }}
              Icon={Lock}
              name={i18n.t('release-locked.heading')}
              description={i18n.t('release-locked.description')}
            />
          )}
          {imageUploadError || values?.image_upload_error && (
            <Alert color="danger">{i18n.t('image-upload-error')}: {i18n.t(imageUploadError ?? values?.image_upload_error)}</Alert>
          )}
          {release?.reject_message && (
            <Alert key={release?.reject_message} color="danger">{i18n.t('release-was-rejected')}: {release?.reject_message}</Alert>
          )}
          {errors?.map(error => (
            <Alert key={error.description} color="danger">{error.description}</Alert>
          ))}
          <ArtworkField
            readOnly={values?.locked}
            label={i18n.t('artwork')}
            errors={getFieldErrors('image')}
            imageUrl={imageUrl ??  values?.image_url}
            onChange={handleImageChange}
          />
          <TextField
            name="name"
            readOnly={values?.locked}
            errors={getFieldErrors('name')}
            defaultValue={values?.name}
            label={i18n.t('name')}
            placeholder={i18n.t("enter-release-name")}
            onChange={(event) => setValues({ ...values, [event.target.name]: event.target.value  })}
          />
          <TextField
            name="version"
            readOnly={values?.locked}
            label={i18n.t('version')}
            defaultValue={values?.version}
            placeholder={i18n.t("enter-release-version")}
            onChange={(event) => {
              return setValues({ ...values, ['version']: event.target.value })
            }}
            info={i18n.t('version-field-example')}
          />
          <TextField
            name="released"
            readOnly={values?.locked}
            errors={getFieldErrors('released')}
            type="date"
            label={i18n.t('release-date')}
            defaultValue={values?.released?.split('T')[0]}
            placeholder={i18n.t("enter-release-date")}
            onChange={(event) => {
              return setValues({ ...values, released: event.target.value })
            }}
          />
          <TextField
            name="upc"
            readOnly={values?.locked}
            errors={getFieldErrors('upc')}
            label={i18n.t('upc')}
            defaultValue={values?.upc}
            placeholder={i18n.t("enter-upc-code")}
            onChange={(event) => setValues({ ...values, [event.target.name]: event.target.value })}
          />
          <SelectRelatedNode
            label={i18n.t('genre')}
            readOnly={values?.locked}
            errors={getFieldErrors('primary_genre')}
            ownerNodeId={null}
            nodeType="genre"
            ownerNodeType="release"
            addLabel={i18n.t("add-release-genre")}
            placeholder={i18n.t('enter-release-genre')}
            allLabel={i18n.t('all-genres')}
            ownLabel={i18n.t('release-s-genres')}
            node={values?.primary_genre}
            onChange={(primary_genre) => setValues({ ...values, primary_genre })}
          />
          {manager?.type === 'organization' && (
            <SelectRelatedNode
              readOnly={values?.locked}
              label={i18n.t('label')}
              ownerNodeId={values?.id}
              nodeType="label"
              ownerNodeType="release"
              addLabel={i18n.t('add-label')}
              placeholder={i18n.t('enter-label')}
              allLabel={i18n.t('all-labels')}
              ownLabel={i18n.t('track-s-label')}
              node={values?.label}
              onChange={(label) => setValues({ ...values, label })}
            />
          )}
          <SelectRelatedNodes
            readOnly={values?.locked}
            label={i18n.t('artists')}
            errors={getFieldErrors('release_artists')}
            ownerNodeId={null}
            isSubject={() => true}
            junctionNodeColumns={[
              /*{
                id: 'primary',
                type: 'bool',
                label: i18n.t('primary'),
                onLabel: i18n.t('primary'),
                offLabel: i18n.t('primary')
              }*/
            ]}
            nodeType="artist"
            ownerNodeType="release"
            addLabel={"add-artist"}
            allLabel={i18n.t('all-artists')}
            ownLabel={i18n.t('release-s-artists')}
            nodes={values?.release_artists ?? []}
            onChange={(release_artists) => setValues({ ...values, release_artists })}
          />
          <SelectRelatedNodes
            readOnly={values?.locked}
            errors={getFieldErrors('tracks')}
            label={i18n.t('tracklist')}
            ownerNodeId={null}
            nodeType="recording"
            showIndexes={true}
            hasPlayer={true}
            columns={[
              {
                id: 'version',
                type: 'text',
                name: i18n.t('version'),
                render: ({ value }) => <Typography>{value.version}</Typography>
              },
              {
                id: 'recording_artists',
                type: 'belongsToMany',
                name: i18n.t('artists'),
                render: ({ value }) => <Typography>{value.artist.name}</Typography>
              }
            ]}
            ownerNodeType="release"
            addLabel={"add-track"}
            allLabel={i18n.t('all-recordings')}
            ownLabel={i18n.t('release-s-tracks')}
            nodes={values?.tracks ?? []}
            onChange={(tracks) => setValues({ ...values, tracks })}
          />
          <VBox sx={{ display: canEditCopyrightFields ? 'flex' : 'none' }}>
            <CopyrightField
              locked={values?.locked}
              name="c_line"
              label="c-line"
              letter="©️"
              defaultYear={values?.c_line_year}
              defaultName={values?.c_line_text}
              onYearChange={(c_line_year) => setValues({ ...values, c_line_year })}
              onNameChange={(c_line_text) => setValues({ ...values, c_line_text })}
            />
            <CopyrightField
              name="p_line"
              letter="Ⓟ"
              locked={values?.locked}
              label={i18n.t('p-line')}
              defaultYear={values?.p_line_year}
              defaultName={values?.p_line_text}
              onYearChange={(p_line_year) => setValues({ ...values, p_line_year })}
              onNameChange={(p_line_text) => setValues({ ...values, p_line_text })}
            />
          </VBox> 
        </VBox>
        <HBox sx={{ gap: 1 }}>
          <Button
            aria-label={submitLabel}
            type="submit"
            disabled={values?.locked}
            startDecorator={<Check />}
            variant="solid"
            loading={saving}
          >
            {submitLabel}
          </Button>
          <Button
            aria-label={i18n.t('distribute')}
            disabled={values?.locked  || !hasImage || !(release?.is_releaseable) || values?.locked}
            startDecorator={<Check />}
            loading={distributing}
            variant="solid"
            onClick={handleDistributeClick}
          >
            {i18n.t('distribute')}
          </Button>
          {actions}          
          <Box sx={{ flex: 1 }} />
          
        </HBox>
      </Box> 
    </form>
  )
}

ReleaseForm.propTypes = {
  values: PropTypes.object,
  submitLabel: PropTypes.string,
  imageUploadError: PropTypes.string,
  distributing: PropTypes.bool,
  onDistribute: PropTypes.func,
  release: PropTypes.object,
  saving: PropTypes.bool,
  actions: PropTypes.node,
  onSubmit: PropTypes.func,
  onValuesChange: PropTypes.func
}

export function EditReleaseView({
  releaseId
}) {
  const manager = useManager()
  const [saving, setSaving] = useState(false)
  const [distributing, setDistributing] = useState(false)

  const [imageUploadError, setImageUploadError] = useState(null)

  const { enqueueSnackbar } = useSnackbar()
  const [values, setValues] = useState(null)

  const { data: release, refetch, isLoading, error, isError } = useResource({
    queryKey: [manager?.type, manager?.id, 'release', releaseId],
    path: `/${manager?.type}/${manager?.id}/release/${releaseId}`,
    enabled: !!manager
  })

  const saveRelease = async (values) => {
    const image = values.image;
    delete values["image_url"];
    await updateRelease(manager?.type, manager?.id, releaseId, values);
    if (image) {
      try {
        await uploadCoverArtToRelease(releaseId, image);
      } catch (e) {
        setImageUploadError(e.toString())
        throw e;
      }
    }
  };

  const mutation = useMutation({
    mutationFn: saveRelease,
    onSuccess: () => {
      refetch()
      enqueueSnackbar('Release successfully saved');
    },
    onMutate: () => {
      setSaving(true);
    },
    onError: (error) => {
      console.error(error);
      enqueueSnackbar('Error saving release', { variant: 'error' });
    },
    onSettled: () => {
      setSaving(false);
    }
  });

  const handleSubmit = (values) => {
    mutation.mutate(values);
    return false;
  };

  useEffect(() => {
    if (release) {
      setValues({
        ...release
      })
    }
  }, [release])

  const handleDistributeClick = event => {
    event.preventDefault()
    const image = values.image

    setDistributing(true)
    if (image) {
      uploadCoverArtToRelease(releaseId, image).then((result) => {
        if (result.error) {
          setImageUploadError(result.error)
          setDistributing(false)
          enqueueSnackbar(i18n.t(result.error))
          setImageUploadError(result.error)
          return
        }
        submitReleaseForReview(manager?.type, manager?.slug, releaseId).then(() => {
          setDistributing(false)
          enqueueSnackbar(i18n.t('sent-for-distribution'))
        }).catch(e => {
          setDistributing(false)
          console.error(e)
          enqueueSnackbar(e.toString())
        })
      }).catch(e => {
        setImageUploadError(e)
        enqueueSnackbar(i18n.t('error-uploading-image-to-small'))
      })
    } else {  
      submitReleaseForReview(manager?.type, manager?.slug, releaseId).then(() => {
        setDistributing(false)
        enqueueSnackbar(i18n.t('sent-for-distribution'))
      }).catch(e => {
        setDistributing(false)
        console.error(e)
      })
    }
    return false
  }

  if (error) {
    return (
      <ErrorPage onTryAgainClick={() => refetch()} />
    ) 
  }

  if (isLoading) {
    return (
      <LoadingPage />
    )
  }

  console.log(error, isError)
  return (
    <Container>
      <h1>{i18n.t('edit-release')}</h1>
      <ReleaseForm
        onSubmit={handleSubmit}
        saving={saving}
        release={release}
        imageUploadError={imageUploadError}
        values={values}
        distributing={distributing}
        submitLabel={i18n.t('save-release')}
        onDistribute={handleDistributeClick}
      />
      
    </Container>
  )   
}

EditReleaseView.propTypes = {
  releaseId: PropTypes.string
}

export default EditReleasePage;